import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { collect } from 'collect.js';
import LoadingScreen from '../components/loading/LoadingScreen';
import useApiClient from '../hooks/api/useApiClient';
import useAuth from '../hooks/auth/useAuth';
import { COOKIES, RESERVATION_STATUS_TYPE, WEBVIEW_COMMUNICATOR_COMMANDS } from '../services/exports/Constants';
import useAppCookies from '../hooks/utility/useAppCookies';
import ReduxHooks from '../store/ReduxHooks';
import { actionCreators } from '../store/actions';
import usePermissions, { ManagerPermission, ManagerRole } from '../hooks/auth/usePermissions';
import {
  pickFranchiseToPreselect,
  shouldPreselectFranchise,
  shouldReloadFranchiseCompany,
} from '../services/tenant/tenantSelection';
import WebViewCommunicator from '../services/helpers/WebViewCommunicator';
import { ScreenEnum } from '../hooks/navigation/Screens';
import Analytics from '../services/analytics/Analytics';
import {
  RETIRED_PROFILE_PROPERTIES,
  applyAnalyticsContext,
  buildAnalyticsContext,
  buildProfileTraits,
} from '../services/analytics/analyticsContext';
import { operatorLanguages } from '../services/localization/brandLanguages';
import useBrand from '../hooks/context/useBrand';

export const InitialDataContext = createContext(null);

export default function InitialDataProvider({ children }) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Providers:InitialDataProvider' });

  const brand = useBrand();

  const { setCookie, removeCookies } = useAppCookies();

  const { initialData } = useSelector((state) => state);
  const { company } = useSelector((state) => state.currentCompany);
  const { franchise } = useSelector((state) => state.currentFranchise);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { isLoggedIn, logOut } = useAuth();
  const { isRole, hasGlobalAccess, isMerchant, canAccessScreen, can, primaryRole } = usePermissions();
  const { InitialDataManager, FranchisesManager, CurrentCompanyManager, CompaniesManager, OpsQueueManager } =
    useApiClient();
  const { ReservationsManager } = useApiClient({
    baseUrl: import.meta.env.VITE_RESERVATION_API_BASE_URL,
    autoLogOut: false,
  });

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [loading, setLoading] = useState(isLoggedIn);
  const [preselectingCompany, setPreselectingCompany] = useState(false);

  useEffect(() => {
    isLoggedIn && loadInitialData();
  }, [isLoggedIn]);

  useEffect(() => {
    if (!company?.id) {
      setLoading(true);
      setTimeout(() => setLoading(false), 300);
    }

    company?.id && initialDataLoaded && !preselectingCompany && loadCompanyData();
  }, [initialDataLoaded, preselectingCompany, company?.id]);

  useEffect(() => {
    initialDataLoaded && loadAgentOpsBadge();
  }, [initialDataLoaded, company?.id]);

  useEffect(() => {
    if (!initialDataLoaded) {
      return;
    }

    if (!hasGlobalAccess || !serviceProvider?.id) {
      ReduxHooks.dispatch(actionCreators.currentServiceProvider.set(initialData.profile?.service_provider));
    }

    if (isMerchant) {
      const selectedCompany =
        collect(initialData.profile?.company?.franchise?.companies ?? []).firstWhere('id', company?.id) ??
        initialData.profile?.company;

      selectedCompany && ReduxHooks.dispatch(actionCreators.currentCompany.set(selectedCompany));
    }
    // profile.id in the deps: initialDataLoaded stays true across a
    // logout/login within the same SPA session, so a user switch must
    // re-trigger the tenant sync for the NEW profile.
  }, [initialDataLoaded, initialData.profile?.id]);

  // Language on load: the manager's own account preference wins, and without one the
  // outlet's configured language applies — so an English-speaking manager keeps English
  // while everyone else follows the restaurant. Before login the brand decides (see
  // BrandProvider / brandLanguages).
  //
  // `profile.lang` is managers.lang and is NULL until the manager actually picks a
  // language, which is what makes the fall-through to `company.language` reachable. It
  // used to be NOT NULL and written at account creation, so this `??` never fell through
  // and the outlet language reached the storefront and nothing else.
  //
  // Filtered through the partner's languages plus the outlet's own (operatorLanguages):
  // brand alignment still keeps a German session off a Spanish partner's screens, but it
  // must not discard the language this outlet was deliberately configured with.
  //
  // `company.language` is the raw column, so it is null while the outlet is inheriting
  // from its group or partner — which is exactly when the brand default is already the
  // right answer.
  //
  // Applied through i18next directly: the value came from the server, so persisting it
  // back to the account would be a pointless round trip.
  useEffect(() => {
    if (!initialDataLoaded) {
      return;
    }

    const preferred = initialData.profile?.lang ?? company?.language;
    const offered = operatorLanguages(brand, company?.language);

    if (preferred && offered.includes(preferred) && i18n.language?.split(/[-_]/)[0] !== preferred) {
      i18n.changeLanguage(preferred);
    }
  }, [initialDataLoaded, initialData.profile?.lang, company?.language, i18n]);

  useEffect(() => {
    preselectCompany();
  }, [serviceProvider?.id]);

  useEffect(() => {
    WebViewCommunicator.sendMessage({
      type: WEBVIEW_COMMUNICATOR_COMMANDS.REQUEST_ON_BEHALF_OF,
      payload: {
        company_id: company?.id,
        service_provider_id: serviceProvider?.id,
      },
    });
  }, [company?.id, serviceProvider?.id]);

  useEffect(() => {
    if (typeof company?.storefront_store_id === 'number' && isLoggedIn) {
      setCookie(COOKIES.ON_BEHALF_OF_STORE, company?.storefront_store_id, {
        expires: moment().add(399, 'days').toDate(),
      });
      // Publish this admin dashboard's own host so the storefront dashboard's "Admin
      // Dashboard" back-link returns to the partner's own admin URL (apex-scoped, exactly
      // like ON_BEHALF_OF_STORE above). FOO-4519 / FOO-4497.
      setCookie(COOKIES.ADMIN_DASHBOARD_HOST, window.location.origin, {
        expires: moment().add(399, 'days').toDate(),
      });
    } else {
      removeCookies([COOKIES.ON_BEHALF_OF_STORE, COOKIES.ADMIN_DASHBOARD_HOST]);
    }
  }, [company?.storefront_store_id, isLoggedIn]);

  useEffect(() => {
    moment.tz.setDefault(company?.timezone ?? serviceProvider?.regional_config?.timezone);
  }, [company?.timezone, serviceProvider?.regional_config?.timezone]);

  useEffect(() => {
    if (!initialData.profile?.id) {
      return;
    }

    // IDs and enums only (PRD: no personal data in any profile property) — the
    // email and name this used to send are removed from existing profiles too.
    Analytics.identify(initialData.profile.id, buildProfileTraits(initialData.profile));
    Analytics.unsetProfileProperties(RETIRED_PROFILE_PROPERTIES);
    // Once per signed-in user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData.profile?.id]);

  useEffect(() => {
    Analytics.register({
      company_id: company?.id ?? null,
      franchise_id: company?.franchise_id ?? franchise?.id ?? null,
      service_provider_id: serviceProvider?.id ?? null,
    });
  }, [company?.id, company?.franchise_id, franchise?.id, serviceProvider?.id]);

  // The WLP super properties and the partner / restaurant groups (Mixpanel V1),
  // re-applied whenever the tenant context moves: a company switch replaces
  // `restaurant_id`, leaving the restaurant clears it, and logout's reset() clears
  // everything. Only for a signed-in user — the login page must not carry the
  // previous session's context.
  useEffect(() => {
    if (!initialData.profile?.id) {
      return;
    }

    applyAnalyticsContext(buildAnalyticsContext({ serviceProvider, company, primaryRole }));
    // The fields the context reads: the records change identity on every refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialData.profile?.id,
    serviceProvider?.id,
    serviceProvider?.is_master,
    serviceProvider?.partner_type,
    serviceProvider?.is_test,
    company?.id,
    company?.restaurant_source,
    primaryRole,
  ]);

  const refresh = async () => {
    const response = await InitialDataManager.get();
    await loadReservations(response.data);
    await preselectFranchise(response.data);

    if (!response.success && response.status === 403) {
      toast.error(t('toasts.verification_pending'));

      return logOut();
    }

    return response;
  };

  const loadInitialData = async () => {
    await refresh();
    setInitialDataLoaded(true);

    if (!company?.id) {
      setLoading(false);
    }
  };

  const loadCompanyData = async () => {
    setLoading(true);
    await CurrentCompanyManager.get();
    await loadReservations(initialData);

    setTimeout(() => setLoading(false), 500);
  };

  const loadAgentOpsBadge = async () => {
    if (!canAccessScreen(ScreenEnum.AgentOpsQueue) || !can(ManagerPermission.ManageAiSuggestions)) {
      return;
    }

    // Pending count for the sidebar badge — scoped to the selected company when
    // one is active in the sidebar, otherwise across all accessible companies.
    const { success, data } = await OpsQueueManager.list({
      page: 1,
      company_ids: company?.id ? [company.id] : [],
    });

    success &&
      ReduxHooks.dispatch(
        actionCreators.sideBar.setBadge({
          [ScreenEnum.AgentOpsQueue]: data?.meta?.total ?? 0,
        }),
      );
  };

  const loadReservations = async (initialDataResponse) => {
    // A failed /initial-data answers with an error body and no profile.
    if (!initialDataResponse?.profile) {
      return;
    }

    if (!initialDataResponse.profile.roles.includes(ManagerRole.StoreAdmin) && !company.id) {
      return;
    }

    const { success, data } = await ReservationsManager.getReservations({
      franchise_id: company.id ? company.franchise_id : franchise?.id,
      service_provider_id: serviceProvider?.id,
      status: [RESERVATION_STATUS_TYPE.PENDING, RESERVATION_STATUS_TYPE.UPDATE_PENDING],
    });

    success &&
      ReduxHooks.dispatch(
        actionCreators.sideBar.setBadge({
          [ScreenEnum.ReservationsOverview]: data.total,
        }),
      );
  };

  const preloadSameCompany = async () => {
    // Retried before giving up: clearing the selection is drastic — every
    // tenant-scoped guard then bounces the user to the dashboard — and the read
    // can fail for reasons that have nothing to do with access. A company's uuid
    // reaches the SSO service that `LoadACL` reads from via a QUEUED job, so a
    // just-created restaurant answers 404 for a moment while plainly existing,
    // and a network blip looks identical to "you may not see this".
    //
    // Two attempts, so a settled genuine 404 still clears the selection promptly.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const { success } = await CompaniesManager.show(company?.id);

      if (success) {
        return true;
      }

      if (attempt === 0) {
        await new Promise((resolve) => {
          setTimeout(resolve, 750);
        });
      }
    }

    ReduxHooks.dispatch(actionCreators.currentCompany.reset());

    return false;
  };

  const preselectFranchise = async (initialDataResponse) => {
    if (!initialDataResponse) {
      return;
    }

    if (!shouldPreselectFranchise(initialDataResponse.profile, initialDataResponse.franchise_acl)) {
      return;
    }

    const { success, data } = await FranchisesManager.get();

    if (!success) {
      return toast.error('Failed to load data');
    }

    const franchiseToPreselect = pickFranchiseToPreselect(data.data, franchise);

    if (!franchiseToPreselect) {
      return;
    }

    ReduxHooks.dispatch(actionCreators.currentFranchise.set(franchiseToPreselect));

    if (shouldReloadFranchiseCompany(franchiseToPreselect, company?.id)) {
      await CompaniesManager.show(collect(franchiseToPreselect.companies).first()?.id);
    }
  };

  const preselectCompany = async () => {
    if (!company?.id && !isRole(ManagerRole.ContentManager)) {
      return;
    }

    setPreselectingCompany(true);

    if (company?.id && company?.service_provider_id === serviceProvider?.id && (await preloadSameCompany())) {
      return setPreselectingCompany(false);
    }

    const { success, data } = await CompaniesManager.get({
      page: 1,
      limit: 1,
    });

    if (!success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.reset());

      return setPreselectingCompany(false);
    }

    const { success: detailsSuccess } = await CompaniesManager.show(data.data.id);

    if (!detailsSuccess) {
      ReduxHooks.dispatch(actionCreators.currentCompany.reset());
    }

    setPreselectingCompany(false);
  };

  const context = useMemo(
    () => ({
      data: initialData,
      refresh,
      loading,
      setLoading,
    }),
    [loading, initialData, franchise],
  );

  return (
    <InitialDataContext.Provider value={context}>
      {loading || preselectingCompany ? <LoadingScreen /> : children}
    </InitialDataContext.Provider>
  );
}
